import type { FieldArrayRenderProps } from 'formik';
import { FormikProvider, useFormikContext } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import DrilldownItem from '@components/DrilldownItem';
import { FormikFhirDateField } from '@components/FormikFields';
import Icon from '@components/Icon/Icon';

import { useCuration } from '@hooks/useCuration';

import { ColumnLayout } from '@layouts/DrilldownStyle';

import type { FamilyMemberCondition, FormValues } from '@models/family-history';

import { formatFhirDate } from '@utils/fhir';

const messages = defineMessages({
  condition: {
    id: 'familyHistoryDrilldown.condition',
    defaultMessage: 'Condition {index}',
  },
  dateDiagnosed: {
    id: 'familyHistoryDrilldown.dateDiagnosed',
    defaultMessage: 'Date Diagnosed',
  },
  ageDiagnosed: {
    id: 'familyHistoryDrilldown.ageDiagnosed',
    defaultMessage: 'Age Diagnosed',
  },
});

const FamilyConditionsForm: React.FC<{
  arrayHelpers: FieldArrayRenderProps;
  value: FamilyMemberCondition;
  index: number;
}> = ({ arrayHelpers, value, index = 0 }) => {
  const { formatMessage } = useIntl();
  const {
    state: { isCreating, isEditing },
  } = useCuration();

  const formik = useFormikContext<FormValues>();
  const { setFieldValue } = formik;
  const condition = value;

  return (
    <FormikProvider value={formik}>
      <ColumnLayout>
        <DrilldownItem
          label={formatMessage(messages.condition, {
            index: index + 1,
          })}
          name={`conditions.${index}.name`}
          value={condition.name}
        />
        <DrilldownItem
          label={formatMessage(messages.ageDiagnosed)}
          name={`conditions.${index}.ageDiagnosed`}
          value={condition.ageDiagnosed}
        />
        {isCreating || isEditing ? (
          index !== 0 ? (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Icon
                clickable
                name="trash"
                onClick={() => arrayHelpers.remove(index)}
                size="lg"
              />
            </span>
          ) : null
        ) : (
          <DrilldownItem
            label={formatMessage(messages.dateDiagnosed)}
            name={`conditions.${index}.diagnosedYear`}
            readOnly
            value={formatFhirDate(condition.diagnosedYear, { showTime: false })}
          />
        )}
      </ColumnLayout>

      {(isCreating || isEditing) && (
        <FormikFhirDateField
          formats={['date', 'year']}
          label={formatMessage(messages.dateDiagnosed)}
          name={`conditions.${index}.diagnosedYear`}
          onChange={(value) => {
            setFieldValue(`conditions.${index}.diagnosedYear`, value);
          }}
          value={condition.diagnosedYear}
        />
      )}
    </FormikProvider>
  );
};

export default FamilyConditionsForm;
